import {useEffect, useMemo, useState} from "react";
import {useBreakpointValue, useDisclosure, useToast, Box, Spinner, VStack} from "@chakra-ui/react";
import {useSearchParams} from "react-router-dom";
import Cookies from "js-cookie";
import {
    HeaderComponent,
    InfoFetchingError,
    IntentIndicator,
    ListContainer,
    StructureDrawer
} from "../../components/UI";
import {
    OnboardingModal,
    RequestList,
    Role,
    VerificationModal
} from "../../components/RequestListing";
import {useAuth, useChat, useRequest, useVerification} from "../../context";
import {ChatListDrawer, ChatWindow} from "../../components/Chats";
import {Structure, Request, RequestAction, RequestVerification} from "../../models";
import {stateChecker as check} from "../../helper/utils";
import {AFTER_VERIFICATION, DOMAIN_NAME} from "../../helper/constants";

const statuses: Request["status"][] = ["declined", "approved", "cancelled"];

export default function RequestListing() {
    const isXLarge = useBreakpointValue({base: true, xl: false}, {ssr: false});
    const data: Record<string,boolean> = {}
    const req = useRequest();
    const drawers = {chat: useDisclosure(), details: useDisclosure()};
    const modals = {info: useDisclosure(), verif: useDisclosure()};
    const auth = useAuth();
    const chat = useChat();
    const toast = useToast();
    const verif = useVerification();
    const [tab, setTab] = useState(0);
    const [structure, setStructure] = useState<Structure|null>(null);
    const [fetched, setFetched] = useState<Record<string,boolean>>({});
    const [role, setRole] = useState<Role|null>(null);
    const [search] = useSearchParams();
    let requests: Request[] = [];
    let type: string = "";
    const isLoading = useMemo(function () {
        return auth.status === "loading" || check.isPending(req.state.status.overall);
    }, [req.state.status.overall, auth.status]);
    const showChat = useMemo(function () {
        return role !== "resolver" && role !== "participant";
    }, [role]);

    useEffect(function () {
        if (isXLarge && req.state.currentId) {
            drawers.chat.onOpen();
        }
        if (!isXLarge) {
            drawers.chat.onClose();
        }
    }, [isXLarge, req.state.currentId]);
    useEffect(function () {
        let status;
        if (req.currentRequest) {
            chat.actions.setCurrentChat(req.currentRequest.chatID);
            status = req.currentRequest.status;
            if (status === "open") {
                setTab(0);
            } else {
                setTab(1);
            }
        }
    }, [req.currentRequest]);
    useEffect(function () {
        const {currentChat} = chat;
        if (currentChat && !fetched[currentChat.item.id]) {
            data[currentChat.item.id] = true;
            chat.fetchMessages(currentChat.item.id)
            setFetched(Object.assign(fetched, data));
        }
    }, [chat.currentChat, fetched]);
    useEffect(function () {
        const val  = {id: search.get("id"), role: search.get("role")};
        if (!auth.user) {
            return;
        }
        req.getRequests();
        if (val.id) {
            req.selectRequest(val.id);
        }
        if (val.role && ["resolver", "participant"].includes(val.role)) {
            setRole(val.role as Role);
            modals.info.onOpen();
        }
    }, [auth.user]);
	useEffect(function () {
        let status: "error"|"success"|undefined;
        if (check.isError(chat.state.loadingState)) {
            status = "error";
        }
        if (check.isSuccess(chat.state.loadingState)) {
            status = "success";
        }
		if (chat.state.message && status) {
			toast({
                containerStyle: {whiteSpace: "pre-line", lineHeight: "1"},
				description: chat.state.message.desc,
                duration: 5000,
				isClosable: true,
                position: "top",
                status,
                title: chat.state.message.title,
                onCloseComplete: chat.actions.clearError
			});
		}
	}, [chat.state.loadingState, chat.state.message]);
    useEffect(function () {
        const {requests} = req.state;
        const {chats} = chat.state;
        const ids = requests.filter(
            (r) => !chats.map((c) => c.id).includes(r.chatID)
        ).map((r) => r.chatID);
        if (ids.length > 0 || chats.length <=0) {
            chat.fetchChats();
        }
    }, [req.state.requests]);
    useEffect(function () {
        let id;
        let unreads;
        const {currentChat} = chat;
        if (currentChat) {
            id = currentChat.item.id;
            unreads = currentChat.unread.map(function (m) {
                return {messageID: m.id, timestamp: m.timestamp};
            });
            if (unreads.length > 0) {
                chat.acknowledge(id, unreads)
            }
        }
    }, [chat.currentChat]);
    useEffect(function () {
        const {actionState} = req.state;
        if (actionState === "pending") {
            handleOpen();
        }
        if (["failed", "verified", "not-sent"].includes(actionState)) {
            setTimeout(handleCompletion, 5000);
        }
    }, [req.state.actionState]);
    useEffect(function () {
        if (req.state.error) {
            toast({
                containerStyle: {whiteSpace: "pre-line", lineHeight: "1"},
                description: req.state.error.desc,
                duration: 5000,
                isClosable: true,
                onCloseComplete: req.actions.setError,
                position: "top",
                status: "error",
                title: req.state.error.title
            });
        }
    }, [req.state.error]);

    function handleMissingIdentify() {
        Cookies.set(
            AFTER_VERIFICATION,
            `${window.location.href}?id=${req.state.currentId}`,
            {domain: DOMAIN_NAME}
        );
        setRole("verification");
        modals.info.onOpen();
    }

    function handlePendingVerification() {
        setRole("verification-pending");
        modals.info.onOpen();
    }

    function handleMissingBiometric() {
        setRole("biometric");
        modals.info.onOpen();
    }

    function checkStatus(status: RequestVerification): boolean {
        let response = true;
        if (status.type === "identity-verified") {
            if (auth.pendingIdentity) {
                handlePendingVerification();
                return false;
            }
            if (!auth.verified) {
                handleMissingIdentify();
                response = false;
            }
        }
        if (status.type === "biometric-setup" && !auth.user?.hasAccess) {
            handleMissingBiometric();
            response = false;
        }
        return response;
    }

    function onAction(action: RequestAction) {
        const types = ["reassign", "resolve", "cancel"];
        const {currentChat} = chat;
        if (action.type === "read-messages" && currentChat && currentChat.unread.length > 0) {
            chat.acknowledge(
                currentChat.item.id,
                currentChat.unread.map(function (msg) {
                    return {messageID: msg.id, timestamp: msg.timestamp};
                })
            );
        }
        if (action.type === "close") {
            search.delete("id");
            chat.actions.setCurrentChat(undefined);
            req.actions.setRequestId(undefined);
        }
        if (action.type === "view_infos") {
            viewDetails();
        }
        if (!auth.user) {
            console.error(`User infos unavailable for ${action.type}`);
            return;
        }
        if (types.includes(action.type)) {
            if (auth.pendingIdentity) {
                handlePendingVerification();
                return;
            }
            if (!auth.verified) {
                handleMissingIdentify();
                return;
            }
            if (!auth.user.hasAccess) {
                handleMissingBiometric();
                return;
            }
            req.submitAction(action).then(function (res) {
                if (res) {
                    req.actions.setAction(action);
                    verif.actions.addIntent(res);
                }
            });
        }
    }

    async function handleOpen() {
        if (!req.currentRequest) {
            return;
        }
        modals.verif.onOpen();
    }

    function handleCompletion() {
        req.resetActions();
        modals.verif.onClose();
    }

    function closeChat() {
        drawers.chat.onClose();
        onAction({type: "close"});
    }

    function closeOnboarding() {
        setRole(null);
        modals.info.onClose();
    }

    function viewDetails() {
        let canCancel: boolean;
        if (req.currentRequest && chat.currentChat && chat.state.user) {
            canCancel = req.currentRequest.creator === chat.state.user.email;
            setStructure({type: "request", data: {
                canCancel,
                chat: chat.currentChat.item,
                checkStatus,
                currentEmail: chat.state.user.email,
                isLoading: check.isPending(req.state.status.actions),
                onAction,
                request: req.currentRequest
            }});
            drawers.details.onOpen();
        }
    }

    if (tab === 0) {
        requests = req.activeRequests;
        type = "pending";
    }
    if (tab === 1) {
        requests = req.archivedRequests;
        type = "archived";
    }

    if (auth.status === "unauthenticated") {
        return <InfoFetchingError reload={auth.fetchUser}/>;
    }

    return <VStack h="100dvb" w="full" alignItems="start" gap="1rem">
        {req.currentRequest && chat.currentChat && <StructureDrawer
            isOpen={drawers.details.isOpen}
            onClose={drawers.details.onClose}
            structure={structure}
        />}
        {req.currentRequest && <VerificationModal
            action={req.state.currentAction}
            isOpen={modals.verif.isOpen}
            onClose={modals.verif.onClose}
            status={req.state.actionState}
        />}
        {role && <OnboardingModal
            role={role}
            isOpen={modals.info.isOpen}
            onClose={closeOnboarding}
        >
        </OnboardingModal>}
        <HeaderComponent
            title="Manage Requests"
            h="auto"
            tabs={["Active", "Archived"]}
            currentTab={tab}
            onSearch={() => {}}
            onTabChange={setTab}
            pb="4px"
            searchbarWidth="min(425px, 100%)"
        />
        {
            (isLoading && !req.state.currentAction) ? (
                <ListContainer
                    alignItems="center"
                    justifyContent="center"
                    flex="2 1 auto"
                    position="relative"
                    mb=".5rem"
                >
                    <Spinner size="md"/>
                </ListContainer>
            ) : <VStack
                    flexDir="row"
                    spacing={4}
                    align="stretch"
                    w="full"
                    pt={{base: 0, lg: "24px"}}
                    justifyContent="start"
                    flex="2 1 auto"
                    overflowY="auto"
                >
                    <RequestList
                        activeId={req.currentRequest?.requestID}
                        requests={requests.map((r) => Object.assign({
                            newItems: chat.unreadChats[r.chatID ?? ""]
                        }, r))}
                        type={type}
                        hasReachMax={true}
                        onSelect={(r) => req.selectRequest(r.requestID)}
                    >
                        <IntentIndicator for="action"/>
                    </RequestList>
                    {showChat && req.currentRequest  && chat.currentChat && <>
                        <ChatListDrawer
                            archived={statuses.includes(req.currentRequest.status)}
                            isOpen={drawers.chat.isOpen}
                            note={req.note}
                            onAction={onAction}
                            onClose={closeChat}
                            participants= {req.currentRequest.participants.length}
                            title={req.currentRequest.title}
                        />
                        <Box display={{base: "none", xl: "flex"}} w="64%">
                            <ChatWindow
                                archived={statuses.includes(req.currentRequest.status)}
                                onAction={onAction}
                                note={req.note}
                                participants= {req.currentRequest.participants.length}
                                title={req.currentRequest.title}
                            />
                        </Box>
                    </>}
            </VStack>
        }
    </VStack>;
};
